import { gql } from "@apollo/client";
import {
  Box,
  Button,
  Flex,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React from "react";
import { RiExternalLinkFill } from "react-icons/ri";
import { GenericError } from "src/components/Feedback/GenericError";
import { FormikSearchSelect } from "src/components/Inputs/SearchSelect";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { formatIsoDateToOrgDate } from "src/services/format";
import { validateWithZod } from "src/services/formValidations";
import * as Url from "src/services/url";
import { FORM_TEMPLATE_TAB_HASH } from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import { onImportCompleteCallback } from "../types";

const FormSchema = z.object({
  formTemplateId: z
    .string({ message: "This field is required" })
    .min(1, { message: "This field is required" }),
});
type FormType = z.infer<typeof FormSchema>;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  targetFormTemplateId: uuid;
  onImportComplete: onImportCompleteCallback;
};

export const ImportFromOtherDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  targetFormTemplateId,
  onImportComplete,
}) => {
  const organization = useOrganization();
  const toast = useAvelaToast();
  const { remoteData } = useRemoteDataQuery<
    GQL.GetFormTemplateForMessageTemplateImport,
    GQL.GetFormTemplateForMessageTemplateImportVariables
  >(
    gql`
      query GetFormTemplateForMessageTemplateImport(
        $organizationId: uuid!
        $targetFormTemplateId: uuid!
      ) {
        form_template(
          where: {
            id: { _neq: $targetFormTemplateId }
            enrollment_period: { organization_id: { _eq: $organizationId } }
            deleted_at: { _is_null: true }
          }
        ) {
          id
          name
          enrollment_period {
            id
            name
          }
          updated_at
        }
      }
    `,
    {
      variables: {
        organizationId: organization.toNullable()?.id ?? "",
        targetFormTemplateId,
      },
      skip: !organization.hasData(),
    }
  );

  const service = FSQ.useImportMessageTemplatesFromOther();
  const handleImportFromOther = React.useCallback(
    async (values: FormType) => {
      toast.closeAll();

      try {
        const { data: result } = await service.mutateAsync({
          path: {
            formTemplateId: targetFormTemplateId,
            sourceFormTemplateId: values.formTemplateId,
          },
        });

        if (!result || !result?.data) {
          toast.error({
            title: "Something went wrong",
            description: "Please try again",
          });
          return onImportComplete({
            errors: ["Something went wrong"],
          });
        }

        if (result.data.errors.length > 0) {
          toast.error({
            title: "Some or all messages were not imported",
            description: "Some or all messages were not imported due to errors",
          });
          return onImportComplete({
            data: result.data.messageTemplates,
            errors: result.data.errors.map((error) => error.message),
          });
        }

        const sourceFormTemplate = remoteData
          .toNullable()
          ?.form_template.find((ft) => ft.id === values.formTemplateId);

        // Success
        toast({
          title: "Imported from existing",
          description: sourceFormTemplate
            ? `Automatic messages imported from ${sourceFormTemplate.name}`
            : `Automatic messages imported`,
          status: "info",
          isClosable: true,
        });

        return onImportComplete({
          data: result.data.messageTemplates,
        });
      } catch (error: any) {
        if (error.error?.message) {
          toast.error({
            title: "Something went wrong",
            description: error.error.message,
          });
        } else {
          toast.genericError();
        }
      } finally {
        onClose();
      }
    },
    [
      onClose,
      onImportComplete,
      remoteData,
      service,
      targetFormTemplateId,
      toast,
    ]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <Formik<FormType>
        initialValues={{ formTemplateId: "" }}
        onSubmit={handleImportFromOther}
        validate={validateWithZod(FormSchema)}
      >
        {(formik) => (
          <ModalContent as={Form} noValidate>
            <ModalHeader>Import form</ModalHeader>
            <ModalBody>
              <Flex direction="column" gap="4">
                <Text>
                  Select a form to import items from it.{" "}
                  <Box fontWeight="bold" as="span">
                    The import will replace any existing content.
                  </Box>
                </Text>
                <RemoteDataView
                  remoteData={remoteData}
                  error={(error) => <GenericError error={error} />}
                  loading={<Skeleton width="100%" height="2rem" />}
                >
                  {(data) => (
                    <FormikSearchSelect
                      name="formTemplateId"
                      label="Form to import form"
                      items={data.form_template.map((ft) => ({
                        key: ft.id,
                        label: ft.name,
                        description: (
                          <>
                            <Text>{ft.enrollment_period.name}</Text>
                            <Text>
                              Last modified on{" "}
                              {formatIsoDateToOrgDate(
                                ft.updated_at,
                                organization
                              )}
                            </Text>
                          </>
                        ),
                        action: (
                          <Tooltip label={`Open ${ft.name} in new tab`}>
                            <Link
                              aria-label={`Open ${ft.name} in new tab`}
                              target="_blank"
                              onClick={(event) => event.stopPropagation()}
                              href={organization
                                .map((org) =>
                                  Url.OrgAdmin.FormTemplates.edit(
                                    org,
                                    ft.enrollment_period.id,
                                    ft.id,
                                    FORM_TEMPLATE_TAB_HASH.AUTOMATIC_MESSAGES
                                  )
                                )
                                .withDefault("#")}
                            >
                              <Icon as={RiExternalLinkFill} />
                            </Link>
                          </Tooltip>
                        ),
                      }))}
                    />
                  )}
                </RemoteDataView>
              </Flex>
            </ModalBody>
            <ModalFooter gap="3">
              <Button
                variant="ghost"
                onClick={onClose}
                isDisabled={formik.isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={formik.isSubmitting}>
                Next
              </Button>
            </ModalFooter>
          </ModalContent>
        )}
      </Formik>
    </Modal>
  );
};
